import { View } from "@novorender/api";
import { HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";

import { getFilePathFromObjectPath, hasDrawingsDocumentSource } from "utils/objectData";

import type { BoundsParts, ObjectDataAccess, SelectionBounds } from "./types";

/**
 * One object's own bounds as a {@link SelectionBounds}, narrowed to what was asked for.
 */
export function objectBoundsAsSelection(bounds: ObjectData["bounds"], parts: BoundsParts): SelectionBounds | undefined {
    if (!bounds) {
        return undefined;
    }
    switch (parts) {
        case "sphere":
            return { sphere: bounds.sphere };
        case "box":
            return { box: bounds.box };
        case "both":
            return bounds;
    }
}

/**
 * Whether the object is a PDF/drawing document — for e.g. a canvas pick result, where
 * only the id is known and the path (and, for extension-less PDFs, the
 * "Procore/DocumentSource" property) has to be read.
 *
 * `false` when the id names nothing, when the read fails, and when no backend is hydrated
 * yet: none of them is a document, and a click cannot wait for one to become available.
 *
 * @param options.knownPdfObjectIds The scene's document ids — Procore's scene-wide
 * discovery (`utils/pdfObjectIds`), which admits a search hit only when its path satisfies
 * the same rule as below. Given one, it answers on its own and nothing is read: a hit is a
 * document, a miss is not. Pass it only once discovery has finished; while it is still
 * outstanding pass `undefined` so the object is read instead (see `useCanvasClickHandler`).
 * @see isPdfOrDrawingObject in `utils/objectData` for the variant that already holds a row.
 */
export async function isPdfOrDrawingObjectId(
    access: ObjectDataAccess | undefined,
    objectId: ObjectId,
    options?: { knownPdfObjectIds?: Pick<Set<ObjectId>, "has">; signal?: AbortSignal },
): Promise<boolean> {
    if (options?.knownPdfObjectIds) {
        return options.knownPdfObjectIds.has(objectId);
    }
    if (!access) {
        return false;
    }
    try {
        const object = await access.getObject(objectId, options?.signal);
        if (!object) {
            return false;
        }
        const filePath = getFilePathFromObjectPath(object.path);
        return filePath ? filePath.toLowerCase().endsWith(".pdf") : hasDrawingsDocumentSource(object.properties);
    } catch {
        return false;
    }
}

export async function getObjectsByIdsOffline(view: View, idArr: ObjectId[], abortSignal: AbortSignal) {
    // pMap doesn't handle big arrays well, so use promise batches instead
    const { data } = view;
    if (!data) {
        return [];
    }
    const BATCH_SIZE = 16;
    const result = [] as HierarcicalObjectReference[];
    for (let i = 0; i < idArr.length; i += BATCH_SIZE) {
        const batch = idArr.slice(i, i + BATCH_SIZE);
        const objects = await Promise.all(batch.map((id) => data.getObjectMetaData(id)));
        if (abortSignal.aborted) {
            return [];
        }
        for (const obj of objects) {
            if (obj) {
                result.push(obj as unknown as HierarcicalObjectReference);
            }
        }
    }
    return result;
}
